import moment from 'moment/moment';
import { BusHistoryUrlFilterKey, BusHistoryUrlFilters, ParsedBusHistoryUrlFilters } from './types/BphTypes';
import { Nullable, Optional } from './types/Optional';

const supportedQueryKeys: BusHistoryUrlFilterKey[] = ['date', 'time', 'routeId', 'stopId', 'directionVariantId', 'displayStops'];
const timeFormats: string[] = ['h:mm A', 'h:mmA', 'hh:mm A', 'H:mm', 'HH:mm'];

function normalizeDate(value: Nullable<string>): Optional<string> {
    if (value === null) {
        return undefined;
    }

    const trimmed: string = value.trim();
    if (!trimmed) {
        return undefined;
    }

    const parsed = moment(trimmed, 'YYYY-MM-DD', true);
    if (!parsed.isValid()) {
        return undefined;
    }

    return parsed.format('YYYY-MM-DD');
}

function normalizeTime(value: Nullable<string>): Optional<string> {
    if (value === null) {
        return undefined;
    }

    const trimmed: string = value.trim();
    if (!trimmed) {
        return undefined;
    }

    const parsed = moment(trimmed.toUpperCase(), timeFormats, true);
    if (!parsed.isValid()) {
        return undefined;
    }

    return parsed.format('h:mm A');
}

function normalizeIdentifier(value: Nullable<string>): Optional<string> {
    if (value === null) {
        return undefined;
    }

    const trimmed: string = value.trim();
    return trimmed || undefined;
}

function parseDisplayStops(value: Nullable<string>): Optional<boolean> {
    if (value === null) {
        return undefined;
    }

    if (value === 'true') {
        return true;
    }

    if (value === 'false') {
        return false;
    }

    return undefined;
}

export function sanitizeBusHistoryUrlFilters(filters: BusHistoryUrlFilters): BusHistoryUrlFilters {
    const sanitized: BusHistoryUrlFilters = {};

    const date: Optional<string> = normalizeDate(filters.date || null);
    if (date) {
        sanitized.date = date;
    }

    const time: Optional<string> = normalizeTime(filters.time || null);
    if (time) {
        sanitized.time = time;
    }

    const routeId: Optional<string> = normalizeIdentifier(filters.routeId || null);
    if (routeId) {
        sanitized.routeId = routeId;
    }

    const stopId: Optional<string> = normalizeIdentifier(filters.stopId || null);
    if (stopId) {
        sanitized.stopId = stopId;
    }

    const directionVariantId: Optional<string> = normalizeIdentifier(filters.directionVariantId || null);
    if (directionVariantId) {
        sanitized.directionVariantId = directionVariantId;
    }

    if (filters.displayStops !== undefined) {
        sanitized.displayStops = filters.displayStops === true;
    }

    return sanitized;
}

function normalizeFilterDependencies(filters: BusHistoryUrlFilters): BusHistoryUrlFilters {
    const normalized: BusHistoryUrlFilters = {
        ...filters,
    };

    if (!normalized.routeId && !normalized.stopId) {
        delete normalized.directionVariantId;
    }

    if (!normalized.directionVariantId) {
        delete normalized.displayStops;
    }

    return normalized;
}

export function serializeBusHistoryUrlFilters(filters: BusHistoryUrlFilters): string {
    const sanitized: BusHistoryUrlFilters = normalizeFilterDependencies(sanitizeBusHistoryUrlFilters(filters));
    const params = new URLSearchParams();

    if (sanitized.date) {
        params.set('date', sanitized.date);
    }
    if (sanitized.time) {
        params.set('time', sanitized.time);
    }
    if (sanitized.routeId) {
        params.set('routeId', sanitized.routeId);
    }
    if (sanitized.stopId) {
        params.set('stopId', sanitized.stopId);
    }
    if (sanitized.directionVariantId) {
        params.set('directionVariantId', sanitized.directionVariantId);
    }
    if (sanitized.displayStops !== undefined) {
        params.set('displayStops', sanitized.displayStops ? 'true' : 'false');
    }

    return params.toString();
}

export function toBusHistoryUrlSearch(filters: BusHistoryUrlFilters): string {
    const serialized: string = serializeBusHistoryUrlFilters(filters);
    return serialized ? `?${serialized}` : '';
}

export function parseBusHistoryUrlFilters(search: string): ParsedBusHistoryUrlFilters {
    const normalizedSearch: string = search.startsWith('?') ? search.slice(1) : search;
    const params = new URLSearchParams(normalizedSearch);
    const filters: BusHistoryUrlFilters = {};
    const invalidKeys: BusHistoryUrlFilterKey[] = [];

    const supportedParamsPresent: boolean = supportedQueryKeys.some((key: BusHistoryUrlFilterKey) => params.has(key));

    if (params.has('date')) {
        const date: Optional<string> = normalizeDate(params.get('date'));
        if (date) {
            filters.date = date;
        } else {
            invalidKeys.push('date');
        }
    }

    if (params.has('time')) {
        const time: Optional<string> = normalizeTime(params.get('time'));
        if (time) {
            filters.time = time;
        } else {
            invalidKeys.push('time');
        }
    }

    if (params.has('routeId')) {
        const routeId: Optional<string> = normalizeIdentifier(params.get('routeId'));
        if (routeId) {
            filters.routeId = routeId;
        } else {
            invalidKeys.push('routeId');
        }
    }

    if (params.has('stopId')) {
        const stopId: Optional<string> = normalizeIdentifier(params.get('stopId'));
        if (stopId) {
            filters.stopId = stopId;
        } else {
            invalidKeys.push('stopId');
        }
    }

    if (params.has('directionVariantId')) {
        const directionVariantId: Optional<string> = normalizeIdentifier(params.get('directionVariantId'));
        if (directionVariantId) {
            filters.directionVariantId = directionVariantId;
        } else {
            invalidKeys.push('directionVariantId');
        }
    }

    if (params.has('displayStops')) {
        const displayStops: Optional<boolean> = parseDisplayStops(params.get('displayStops'));
        if (displayStops !== undefined && displayStops !== null) {
            filters.displayStops = displayStops;
        } else if (displayStops === undefined) {
            invalidKeys.push('displayStops');
        }
    }

    return {
        filters: sanitizeBusHistoryUrlFilters(filters),
        hasSupportedParams: supportedParamsPresent,
        invalidKeys: Array.from(new Set(invalidKeys)),
    };
}
